import { useEffect, useState, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageThreadView } from "./MessageThreadView";
import { useTaskThread, useGetOrCreateTaskThread } from "@/hooks/useMessages";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface MessageThreadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  taskTitle?: string;
}

export const MessageThreadModal = ({
  open,
  onOpenChange,
  taskId,
  taskTitle,
}: MessageThreadModalProps) => {
  const isMobile = useIsMobile();
  const { thread, isLoading, createMutation, refetch } = useGetOrCreateTaskThread(taskId, open);
  const [participantNames, setParticipantNames] = useState<Record<string, string>>({});
  const ensureDoneRef = useRef(false);

  // When modal opens and no thread exists, get or create thread (owner + cleaner + team)
  useEffect(() => {
    if (!open || !taskId || thread || isLoading || createMutation.isPending) return;
    if (ensureDoneRef.current) return;
    ensureDoneRef.current = true;

    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_task_message_thread_init", { p_task_id: taskId });
        if (error) throw error;
        if (!data) {
          ensureDoneRef.current = false;
          return;
        }
        if ("thread_id" in data && data.thread_id) {
          await refetch();
          return;
        }
        if ("participants" in data && Array.isArray(data.participants) && data.property_id && data.task_id) {
          await createMutation.mutateAsync({
            property_id: data.property_id,
            task_id: data.task_id,
            participants: data.participants,
          });
          await refetch();
        }
      } catch {
        ensureDoneRef.current = false;
      }
    })();
  }, [open, taskId, thread, isLoading, createMutation.isPending, refetch, createMutation]);

  useEffect(() => {
    if (!open) ensureDoneRef.current = false;
  }, [open]);

  // Fetch participant names
  useEffect(() => {
    if (!thread?.participants || thread.participants.length === 0) return;

    const fetchNames = async () => {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, first_name, last_name")
        .in("id", thread.participants);

      const { data: cleaners } = await supabase
        .from("cleaners")
        .select("user_id, name")
        .in("user_id", thread.participants);

      const names: Record<string, string> = {};
      profiles?.forEach((p: { id: string; full_name?: string | null; first_name?: string | null; last_name?: string | null }) => {
        const full = (p.full_name ?? "").trim();
        const first = (p.first_name ?? "").trim();
        const last = (p.last_name ?? "").trim();
        const display = full || [first, last].filter(Boolean).join(" ").trim();
        if (display) names[p.id] = display;
      });
      cleaners?.forEach((c: { user_id: string; name?: string | null }) => {
        if (c.name && c.user_id) names[c.user_id] = c.name.trim();
      });
      setParticipantNames(names);
    };

    fetchNames();
  }, [thread?.participants]);

  const creating = createMutation.isPending;
  const content = (
    <div className="flex flex-col h-full">
      {isLoading || creating ? (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">{creating ? "Starting conversation…" : "Loading..."}</p>
        </div>
      ) : !thread ? (
        <div className="flex flex-col items-center justify-center h-full text-center p-4">
          <MessageCircle className="w-12 h-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No message thread found</p>
        </div>
      ) : (
        <MessageThreadView threadId={thread.id} participantNames={participantNames} />
      )}
    </div>
  );

  // Full-screen modal for mobile
  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="h-[100dvh] max-h-[100dvh] max-w-full w-full p-0 flex flex-col rounded-none border-0 gap-0"
          style={{ 
            paddingTop: "env(safe-area-inset-top, 0px)",
          }}
        >
          {/* Compact mobile header - single close comes from DialogContent */}
          <DialogHeader className="px-4 py-3 pr-12 border-b shrink-0 bg-card/95 backdrop-blur-sm flex flex-row items-center">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold truncate flex-1 min-w-0">
              <MessageCircle className="w-5 h-5 shrink-0 text-primary" />
              <span className="truncate">{taskTitle || "Messages"}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">{content}</div>
        </DialogContent>
      </Dialog>
    );
  }

  // Side sheet for desktop
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[450px] p-0 flex flex-col">
        <SheetHeader className="p-4 pb-3 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            {taskTitle || "Task Messages"}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-hidden">{content}</div>
      </SheetContent>
    </Sheet>
  );
};
